import { z } from 'zod'
import { widgetFilterSchema, widgetSchema } from '../../common/schema'
import {
  CARD_ELEMENT_ALIGNS,
  CARD_ELEMENT_IDS,
  defaultCardElements,
  normalizeCardElements,
  type CardElementTemplate,
} from '../../common/property-card/card-elements'

export const propertyCardFilterSchema = widgetFilterSchema.extend({
  feed_id: z.number().int().describe('Filter by feed ID'),
  platform: z.string().nullish().describe('Filter by platform UUID'),
  property: z.string().describe('Filter by property slug'),
}).describe('Data filter options for property card')

export type PropertyCardFilter = z.infer<typeof propertyCardFilterSchema>

// #822: property-card renders its own templates, themed through the same
// `--hfse-*` CSS vars PropertyCardWidgetContent plumbs. These 6 keys are the
// ones the card surfaces use; the full 12-key explorer palette adds
// search/button colors a lone card never shows, so we keep this tight set
// (accent stays — the shared widget contract requires it and it feeds
// --hfse-accent).
const propertyCardLightColors = {
  accent: '#f59e0c',
  foreground: '#333333',
  mutedForeground: '#999999',
  cardBackground: '#ffffff',
  cardForeground: '#666666',
  borderColor: '#e5e5e5',
}

const propertyCardDarkColors = {
  accent: '#f59e0c',
  foreground: '#e0e0e0',
  mutedForeground: '#777777',
  cardBackground: '#1a1a1a',
  cardForeground: '#cccccc',
  borderColor: '#444444',
}

const propertyCardColorSchema = (defaults: typeof propertyCardLightColors) => z.object({
  accent: z.string().default(defaults.accent).describe('Accent color'),
  foreground: z.string().default(defaults.foreground).describe('Primary text color'),
  mutedForeground: z.string().default(defaults.mutedForeground).describe('Secondary/muted text color'),
  cardBackground: z.string().default(defaults.cardBackground).describe('Card background color'),
  cardForeground: z.string().default(defaults.cardForeground).describe('Card text color'),
  borderColor: z.string().default(defaults.borderColor).describe('Border color'),
})

// #824: per-element type scale. Defaults sit on a rounded major-second ramp
// (16 / 14 / 13 / 12 / 11) that lands within ~2% of the sizes the templates
// shipped with, so existing cards keep their look. Values are px; the CSS
// derives the overlay-scrim and emphasized-price variants from these with
// fixed ratios, so one knob moves its element consistently across templates.
const propertyCardTypographyDefaults = {
  titleSize: 16,
  locationSize: 12,
  priceSize: 14,
  excerptSize: 13,
  metaSize: 12,
  chipSize: 11,
}

// An out-of-range stored size (autosaved before the form clamp existed, or
// hand-edited) must degrade to the scale default instead of the widget's
// throwing runtime parse blanking the whole card. z.preprocess, NOT
// z.catch: the dashboard form renderer has no ZodCatch branch and renders
// unrecognized wrappers as nothing — .catch made all six inputs vanish
// (codex #849 round 2). ZodEffects is the supported wrapper.
const boundedSize = (fallback: number, description: string) =>
  z.preprocess(
    (v) => (typeof v === 'number' && v >= 8 && v <= 48 ? v : undefined),
    z.number().min(8).max(48).default(fallback),
  ).describe(description)

const propertyCardTypographySchema = z.object({
  titleSize: boundedSize(propertyCardTypographyDefaults.titleSize, 'Property name size in pixels'),
  locationSize: boundedSize(propertyCardTypographyDefaults.locationSize, 'Location line size in pixels (the rating badge follows it)'),
  priceSize: boundedSize(propertyCardTypographyDefaults.priceSize, 'Price row size in pixels; the rate figure scales with it'),
  excerptSize: boundedSize(propertyCardTypographyDefaults.excerptSize, 'Short description size in pixels (Detailed template)'),
  metaSize: boundedSize(propertyCardTypographyDefaults.metaSize, 'Guests / bedrooms / baths row size in pixels'),
  chipSize: boundedSize(propertyCardTypographyDefaults.chipSize, 'Property-type tag size in pixels'),
})

// #824: user-visible card strings, per-locale (same store shape as the
// calendar/amenities widgets — `useTranslation` resolves against it).
// Exported for widgets that reuse the shared card parts (featureParts et al.)
// without carrying their own translations setting (saved-panel, #1143).
export const propertyCardTranslationDefaults = {
  price_from: { en_US: 'From' },
  price_per_night: { en_US: '/ night' },
  view_property: { en_US: 'View property' },
  feature_guest: { en_US: 'guest' },
  feature_guests: { en_US: 'guests' },
  feature_bedroom: { en_US: 'bedroom' },
  feature_bedrooms: { en_US: 'bedrooms' },
  feature_bedrooms_short: { en_US: 'bd' },
  feature_studio: { en_US: 'Studio' },
  feature_bath: { en_US: 'bath' },
  feature_baths: { en_US: 'baths' },
  feature_baths_short: { en_US: 'ba' },
  rating_reviews: { en_US: 'reviews' },
  photo_previous: { en_US: 'Previous photo' },
  photo_next: { en_US: 'Next photo' },
  photo_position: { en_US: 'Photo %1 of %2' },
}

const propertyCardTranslationsSchema = z.object({
  price_from: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.price_from).describe('Word before the nightly rate (e.g. "From")'),
  price_per_night: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.price_per_night).describe('Unit after the nightly rate (e.g. "/ night")'),
  view_property: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.view_property).describe('Link text on the Detailed template'),
  feature_guest: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.feature_guest).describe('Label after the guest count when it is 1'),
  feature_guests: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.feature_guests).describe('Label after the guest count'),
  feature_bedroom: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.feature_bedroom).describe('Label after the bedroom count when it is 1'),
  feature_bedrooms: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.feature_bedrooms).describe('Label after the bedroom count'),
  feature_bedrooms_short: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.feature_bedrooms_short).describe('Abbreviated bedroom label on the Photo Overlay template'),
  feature_studio: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.feature_studio).describe('Shown instead of a bedroom count when the property has none'),
  feature_bath: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.feature_bath).describe('Label after the bath count when it is 1'),
  feature_baths: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.feature_baths).describe('Label after the bath count'),
  feature_baths_short: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.feature_baths_short).describe('Abbreviated bath label on the Photo Overlay template'),
  rating_reviews: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.rating_reviews).describe('Word in the review-count tooltip (e.g. "24 reviews")'),
  photo_previous: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.photo_previous).describe('Screen-reader label on the slideshow back arrow'),
  photo_next: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.photo_next).describe('Screen-reader label on the slideshow forward arrow'),
  photo_position: z.record(z.string(), z.string()).default(propertyCardTranslationDefaults.photo_position).describe('Screen-reader label on a slideshow dot — %1 = photo number, %2 = photo count'),
})

// #825: per-template element order + per-element alignment
// (docs/property-card-elements.md). Each template's list is preprocess-
// wrapped so a half-typed autosave degrades to the designed order instead of
// throwing at render: unknown and duplicate ids drop, ids the template never
// renders drop, missing ids append in default order, a bad align reads as
// `default`. `default` alignment emits nothing, so an untouched widget stays
// byte-identical. The describe() sits INSIDE the wrapper: zod-form's
// child-wins def merge would otherwise clobber it (docs/widget-system.md).
const cardElementSchema = z.object({
  id: z.enum(CARD_ELEMENT_IDS).describe('Card element'),
  align: z.enum(CARD_ELEMENT_ALIGNS).default('default').describe('Horizontal alignment; default keeps the template’s own'),
})

const cardElementList = (template: CardElementTemplate, description: string) =>
  z.preprocess(
    (v) => normalizeCardElements(template, v),
    z.array(cardElementSchema).default(defaultCardElements(template)).describe(description),
  )

const propertyCardElementsSchema = z.object({
  classic: cardElementList('classic', 'Element order and alignment on the Classic template'),
  overlay: cardElementList('overlay', 'Element order and alignment on the Photo Overlay template'),
  detailed: cardElementList('detailed', 'Element order and alignment on the Detailed template'),
})

export const propertyCardWidgetConfig = widgetSchema.extend({
  filter: propertyCardFilterSchema,
  template: z.enum(['classic', 'overlay', 'detailed', 'explorer']).default('classic').describe('Card layout. Classic stacks the details under the photo, Photo Overlay lays the name and price over the photo, Detailed adds a short description and a link to the property page, and Search Results matches the card the Explorer widget renders'),
  showImage: z.boolean().default(true).describe('Show the photo slideshow at the top of the card; off removes the photo area entirely'),
  showPrice: z.boolean().default(true).describe('Show the nightly rate on the card'),
  showLocation: z.boolean().default(true).describe('Show the property’s location tags on the card'),
  showFeatures: z.boolean().default(true).describe('Show the guests, bedrooms, and bathrooms row'),
  showPropertyType: z.boolean().default(true).describe('Show the property-type tag (e.g. Villa, Condo) on the card'),
  showExcerpt: z.boolean().default(true).describe('Show the property’s short description under the location'),
  imageAspectRatio: z.enum(['16/9', '4/3', '3/2', '1/1']).default('16/9').describe('Aspect ratio of the card’s photo area'),
  maxImages: z.number().int().min(1).max(20).default(7).describe('Maximum number of photos in the card’s slideshow'),
  overlayGradientStrength: z.enum(['soft', 'strong']).default('soft').describe('How dark the shading behind the text on the photo is'),
  openInNewTab: z.boolean().default(false).describe('Open the property page in a new tab when the card is clicked'),
  lightModeColors: propertyCardColorSchema(propertyCardLightColors).default(propertyCardLightColors).describe('Colors in light mode'),
  darkModeColors: propertyCardColorSchema(propertyCardDarkColors).default(propertyCardDarkColors).describe('Colors in dark mode'),
  typography: propertyCardTypographySchema.default(propertyCardTypographyDefaults).describe('Per-element text sizes'),
  translations: propertyCardTranslationsSchema.default(propertyCardTranslationDefaults).describe('The translations of the widget'),
  elements: propertyCardElementsSchema.default({}).describe('Order and horizontal alignment of the card’s data elements, per template; the template’s designed order until changed'),
})

export type PropertyCardWidgetConfig = z.infer<typeof propertyCardWidgetConfig>
